/**
 * Gemeinsamer Analytics-Event-Katalog (2026-09-22) – identisch mit Backend
 * (src/core/content/analytics-event-catalog.js) und App (analytics_events.dart).
 * PURE: Konstanten + Ableitung des Inhalts-Kontexts aus dem Pfad.
 */

export const AnalyticsEvents = {
  pageView: 'page_view',
  tournamentRegistrationComplete: 'tournament_registration_complete',
  listingFollow: 'listing_follow',
  listingUnfollow: 'listing_unfollow',
  favoriteAdd: 'favorite_add',
  favoriteRemove: 'favorite_remove',
  search: 'search',
  predictionSubmit: 'prediction_submit',
  postCreate: 'post_create',
  messageSend: 'message_send',
  externalLinkClick: 'external_link_click',
  share: 'share',
  sponsorBannerImpression: 'sponsor_banner_impression',
  sponsorBannerClick: 'sponsor_banner_click',
} as const;

export type ContentType =
  | 'tournament' | 'club' | 'pub' | 'shop' | 'series' | 'league' | 'association'
  | 'news' | 'prediction_game' | 'pro_tournament' | 'player' | 'ranking' | 'other';

export type SponsorPlacement = 'detail_strip' | 'detail_tab' | 'detail_cover' | 'overview_cover' | 'home_cover';

export interface ContentContext {
  content_type: ContentType;
  content_slug: string | null;
}

/** Pfad-Präfix → Inhaltstyp (Reihenfolge: spezifische zuerst) */
const CONTENT_PATHS: Array<[RegExp, ContentType]> = [
  [/^\/turniere\/([^/?#]+)/, 'pro_tournament'],
  [/^\/pdc-turniere\/?$/, 'other'],
  [/^\/turnier\/([^/?#]+)/, 'tournament'],
  [/^\/verein\/([^/?#]+)/, 'club'],
  [/^\/spielstaette\/([^/?#]+)/, 'pub'],
  [/^\/shop\/([^/?#]+)/, 'shop'],
  [/^\/serie\/([^/?#]+)/, 'series'],
  [/^\/liga\/([^/?#]+)/, 'league'],
  [/^\/verband\/([^/?#]+)/, 'association'],
  [/^\/news\/([^/?#]+)/, 'news'],
  [/^\/tippspiele\/([^/?#]+)/, 'prediction_game'],
  [/^\/spieler\/([^/?#]+)/, 'player'],
  [/^\/ranglisten\/([^/?#]+)/, 'ranking'],
];

/** PURE: Inhaltstyp + Slug aus einem App-Pfad (ohne Query), sonst { other, null } */
export const contentFromPath = (path: string | null | undefined): ContentContext => {
  const clean = String(path ?? '').split('?')[0].split('#')[0];
  for (const [re, type] of CONTENT_PATHS) {
    const m = clean.match(re);
    if (m) return { content_type: type, content_slug: m[1] ? decodeURIComponent(m[1]) : null };
  }
  return { content_type: 'other', content_slug: null };
};

/** PURE: Host eines externen Links (ohne www), null bei ungültig */
export const hostOf = (url: string | null | undefined): string | null => {
  try {
    const u = new URL(String(url ?? ''), 'https://dartcloudapp.de');
    return u.hostname.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
};

/** PURE: ist der Link extern (anderer Host als dartcloudapp.de)? */
export const isExternalHref = (href: string | null | undefined): boolean => {
  const raw = String(href ?? '').trim();
  if (!/^https?:\/\//i.test(raw)) return false;
  const host = hostOf(raw);
  return !!host && host !== 'dartcloudapp.de';
};

/** PURE: Link-Art aus Host/Kontext (Shop/Tickets/Sponsor/Website/other) */
export const classifyExternalLink = (href: string, hint?: string | null): 'website' | 'shop' | 'tickets' | 'sponsor' | 'other' => {
  const h = String(hint ?? '').toLowerCase();
  if (h.includes('sponsor')) return 'sponsor';
  if (h.includes('ticket') || /ticket|eventim/i.test(href)) return 'tickets';
  if (h.includes('shop') || /shop|store|amazon/i.test(href)) return 'shop';
  if (h.includes('website') || h.includes('webseite')) return 'website';
  return 'other';
};

export default AnalyticsEvents;
